<script setup lang="ts">
import { ExportButton } from '@sancare/typescript-commons'
import { computed, ref } from 'vue'

import { filterAvailableColumns, filterDisplayedColumns } from '@/stay-list/stay-list-table/helpers/columnFilters'
import McoStayListColumns from '@/stay-list/stay-list-table/mco/StayListColumns'
import SmrStayListColumns from '@/stay-list/stay-list-table/smr/StayListColumns'
import { useStore } from '@/store'
import { ModeEnum, PmsiTypeEnum } from '@/store/modules/settings/types'

const PmsiStayListColumns = {
  [PmsiTypeEnum.MCO]: McoStayListColumns,
  [PmsiTypeEnum.SMR]: SmrStayListColumns,
}

const props = withDefaults(defineProps<{
  request: object
  mode: ModeEnum
  pmsiType?: PmsiTypeEnum
}>(), {
  pmsiType: PmsiTypeEnum.MCO
})

const emit = defineEmits<{
  (e: 'error', message: string)
}>()

const store = useStore()

const isExporting = ref(false)

const userColumns = computed(() => store.state.settings.userSettings.stayListColumns)
const pmsiCountry = computed(() => store.state.settings.pmsi.country)

async function exportStays() {
  isExporting.value = true
  emit('error', null)

  const exportUrl = new URL('/api/export/search-csv', window.location.href)
  const availableColumns = PmsiStayListColumns[props.pmsiType]
  const modeColumns = filterAvailableColumns(
    availableColumns,
    {
      pmsiCountry: pmsiCountry.value,
      mode: props.mode
    }
  )
  const displayedColumns = filterDisplayedColumns(modeColumns, userColumns.value)

  const requestData = {
    mode: props.mode,
    request: props.request,
    columnList: Object.keys(displayedColumns),
  }

  try {
    /* global RequestInit */
    const requestOptions: RequestInit = {
      method: 'POST',
      credentials: 'same-origin',
      headers: new Headers({
        'Cache-Control': 'no-cache, no-store, must-revalidate, max-age=0',
        expires: '0',
        pragma: 'no-cache',
        'X-Requested-With': 'XMLHttpRequest',
      }),
      body: JSON.stringify(requestData),
    }

    const response = await fetch(exportUrl, requestOptions)
    const fileName = getFileNameFromResponse(response)
    const downloadUrl = window.URL.createObjectURL(await response.blob())
    downloadFile(downloadUrl, fileName)
  } catch {
    emit('error', 'Une erreur est survenue lors de l\'export.')
  } finally {
    isExporting.value = false
  }
}

function getFileNameFromResponse(response: Response) {
  const contentDispositionHeader = response.headers.get('Content-Disposition')
  const matchFileName = contentDispositionHeader.match(/filename="?([^"]+)"?/)
  return matchFileName && matchFileName[1] ? matchFileName[1] : 'stay_list_export.xlsx'
}

function downloadFile(downloadUrl: string, fileName: string) {
  const link = document.createElement('a')
  link.href = downloadUrl
  link.download = fileName
  link.setAttribute('target', '_blank')
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.URL.revokeObjectURL(downloadUrl)
}
</script>

<template>
  <div class="btn sancare-btn btn-fixed-width">
    <export-button
      @confirm="exportStays()"
    >
      <template #button>
        <div>
          <div
            v-if="isExporting"
            class="loader loader-xs loader-purple"
          />
          <span v-else>Exporter</span>
        </div>
      </template>
    </export-button>
  </div>
</template>
